import { Injectable } from '@angular/core';
import { BaseParamsService } from '../base-params.service';
import { IdentifierType, MemberIdentifier, MemberInterface } from '../_models/member-interface';
import { SessionService } from '../session/session.service';
import { Observable, of } from 'rxjs';
import { Router } from '@angular/router';
import { MemberTokenType } from '@app/shared/types/member_token_type';
import { catchError, map, tap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import * as fromRoot from '../store/reducers';
import { ClearMember, LoadMember } from '../store/member/member-actions';
import { RecaptchaData } from '@app/core/recaptcha/recaptcha-commons';
import { MembersApiV2Service } from '@app/core/members/members-api-v2.service';
import { ClientSecret } from '@app/core/_models/client-secret';
import { MemberFormData } from '@app/core/members/interfaces';
import { MembersApiV3Service } from './members-api-v3.service';
import { Angulartics2 } from 'angulartics2';

export interface PublicInfoResponse {
  exists: boolean;
  can_login: boolean;
  available_identifiers: string[];
  has_password: boolean;
}


export interface MemberConsent {
  status: boolean;
}

export interface MemberValidateData {
  properties?: any;
  consents?: { [consentName: string]: boolean };
  registration_password?: string;
}

export interface MemberCreateData extends MemberFormData {
  encrypted_reserved_id?: string;
}

export interface CreateMemberRequestData {
  member_form: MemberFormData;
  source: string;
  subsource: string | null;
}

export interface CreateMemberResponse {
  auth_token: string | undefined | null;
  properties?: {
    msisdn?: string;
    email?: string;
  };
}

export interface UnsubscribePayload {
  email: string;
  token: string;
  subunitId: string | null;
}

const convertConsentsToApiModel = (consents: { [consentName: string]: boolean }): {
  [consentName: string]: MemberConsent;
} =>
  Object.entries(consents).reduce((all, [consentKey, value]) => ({
    ...all,
    [consentKey]: { status: value },
  }), {});

@Injectable()
export class MemberService {
  constructor(private router: Router,
              private baseParamsService: BaseParamsService,
              private sessionService: SessionService,
              private store: Store<fromRoot.State>,
              private apiV2Service: MembersApiV2Service,
              private apiV3Service: MembersApiV3Service,
              private analytics: Angulartics2,
  ) {}

  public get(): Observable<Partial<MemberInterface> | null> {
    this.store.dispatch(new LoadMember());
    return this.store.select(fromRoot.selectMember)
      .pipe(
        map((s) => s.member),
      );
  }

  public isMember(
    memberIdentifier: MemberIdentifier,
    recaptchaData?: RecaptchaData,
  ): Observable<PublicInfoResponse> {
    return this.apiV2Service.isMember(memberIdentifier, recaptchaData);
  }

  public sendPasswordByEmail(memberIdentifier?: MemberIdentifier): Observable<string> {
    return this.apiV3Service.sendPasswordByEmail(memberIdentifier)
      .pipe(
        map(({ client_secret }) => client_secret),
      );
  }

  public sendPasswordBySms(memberIdentifier: MemberIdentifier | undefined, recaptchaData: RecaptchaData): Observable<ClientSecret> {
    return this.apiV3Service.sendPasswordBySms(memberIdentifier, recaptchaData);
  }

  /**
   * Works only for msisdn
   */
  public sendRegistrationPassword(memberIdentifier: MemberIdentifier | undefined, recaptchData: RecaptchaData): Observable<unknown> {
    return this.apiV2Service.sendRegistrationPassword(memberIdentifier?.identifier, recaptchData);
  }

  public sendRegistrationPasswordByEmail(memberIdentifier: MemberIdentifier | undefined): Observable<unknown> {
    return this.apiV3Service.sendRegistrationPasswordByEmail(memberIdentifier?.identifier);
  }

  public changePassword(body: unknown): Observable<unknown> {
    return this.apiV2Service.changePassword(body);
  }

  /**
   * Works only for emails!
   */
  public resetPassword(memberIdentifier: MemberIdentifier): Observable<ClientSecret> {
    return this.apiV2Service.resetPassword(memberIdentifier.identifier);
  }

  public verifyToken(identifier: string | undefined,
                     type: MemberTokenType,
                     token: string | null,
                     identifierType: IdentifierType,
  ): Observable<boolean> {
    return this.apiV2Service.verifyToken({
      identifier,
      type,
      token,
      identifierType,
    });
  }

  public setPassword(email: string | null, token: string | null, password: string): Observable<unknown> {
    return this.apiV2Service.setPassword({ email, token, password });
  }

  public create(body: MemberCreateData): Observable<CreateMemberResponse> {
    return this.apiV2Service.create(this.prepareMemberSaveData(body))
      .pipe(
        tap(() => this.baseParamsService.clearAutologinParams()),
      );
  }

  public update(body: MemberCreateData): Observable<any> {
    return this.apiV2Service.update(this.prepareMemberSaveData(body));
  }

  public validate(body: MemberValidateData): Observable<boolean> {
    return this.apiV3Service.validate(this.processMemberFromApp(body));
  }

  public createV3({ email, msisdn, registration_password, consents }: any): Observable<unknown> {
    const data = this.processMemberFromApp({
      properties: { email, msisdn },
      registration_password,
      ignore_required_properties: true,
      consents,
    });
    return this.apiV3Service.create(data);
  }

  public updateConsents(body: any): Observable<unknown> {
    return this.apiV2Service.updateConsents(body);
  }

  public destroy(): Observable<unknown> {
    return this.apiV2Service.destroy()
      .pipe(
        tap(() => {
          this.sessionService.clearAuthToken();
          this.nullifyMember();
          this.analytics.eventTrack.next({
            action: 'delete',
            properties: { category: 'member_change', label: 'Succesfull optout' },
          });
        }),
      );
  }

  public signOut(): Observable<unknown> {
    return this.sessionService.signOut()
      .pipe(
        tap(() => {
          this.nullifyMember();
        }),
      );
  }

  public fetchMember(): Observable<Partial<MemberInterface> | null> {
    return this.apiV2Service.fetchMember()
      .pipe(
        catchError((response) => this.handleGetResponseError(response)),
      );
  }

  public unsubscribeEmail(payload: UnsubscribePayload): Observable<unknown> {
    return this.apiV2Service.unsubscribeEmail(payload);
  }

  public verifyMsisdn(msisdn: string | null, token: string | null): Observable<unknown> {
    return this.apiV2Service.verifyMsisdn(msisdn, token);
  }

  public verifyEmail(email: string | null, token: string | null): Observable<unknown> {
    return this.apiV2Service.verifyEmail(email, token);
  }

  public sendVerificationEmail(email: string, token?: string | null): Observable<unknown> {
    return this.apiV2Service.sendVerificationEmail(email, token);
  }

  public sendVerificationSms(msisdn: string, token?: string | null): Observable<unknown> {
    return this.apiV2Service.sendVerificationSms(msisdn, token);
  }

  public getVerificationStatus(token?: string | null): Observable<any> {
    if (token) { // not authenticated
      return this.apiV2Service.verifyStatus(token);
    } else { // authenticated
      return this.apiV2Service.verifyMyStatus();
    }
  }

  public processMemberToProfileForm(member: Partial<MemberInterface>): Record<string, any> {
    const tmp = JSON.parse(JSON.stringify(member));
    if (tmp.consents) {
      Object.keys(tmp.consents)
        .forEach(consentKey => {
          if (tmp.consents[consentKey]?.status !== undefined) {
            tmp.consents[consentKey] = tmp.consents[consentKey].status;
          }
        });
    } else {
      tmp.consents = {};
    }

    return tmp;
  }

  private nullifyMember(): void {
    this.store.dispatch(new ClearMember());
  }

  private prepareMemberSaveData(body: MemberCreateData): CreateMemberRequestData {
    body.language = this.baseParamsService.language;
    return this.memberParams(this.processMemberFromApp(body));
  }

  private handleGetResponseError(response: Response): Observable<null> {
    if ([401, 460].includes(response.status)) {
      // member not authorized but somehow made his way up this page
      this.sessionService.clearAuthToken();
      this.router.navigate(['/']);
    }

    return of(null);
  }

  private memberParams(body: MemberFormData): CreateMemberRequestData {
    return {
      member_form: body,
      source: this.baseParamsService.source,
      subsource: this.baseParamsService.subsource,
    };
  }

  private processMemberFromApp(member: MemberCreateData): MemberFormData {

    const tmp = JSON.parse(JSON.stringify(member));

    if (tmp.consents) {
      tmp.consents = convertConsentsToApiModel(tmp.consents);
    }

    return tmp;
  }

}
